import { quantitativeSizeToString } from '../../sizes';
import * as selectors from '../selectors';

/**
 * The internal size definition for all inputs in the application that determines the size of
 * the input component and its associated size-related properties, such as inner padding, height,
 * font size, line height, etc.  The size is applied via an HTML data attribute so the theme can
 * apply size-specific styling.
 *
 * This size prop is an internal definition that does not map to MUI's 'size' for any
 * given input component (e.g. {@link Select}, {@link TextInput}, etc.) 1-1:
 *
 * | Input Size | MUI Input Size |
 * | ---------- | -------------- |
 * | small      | (none)         |
 * | medium     | small          |
 * | large      | medium         |
 *
 * Our 'small' size is an extension that does not have a MUI counterpart.
 *
 * @default 'medium'
 */
export enum InputSize {
  Large = 'large',
  Medium = 'medium',
  Small = 'small',
}

export const DefaultInputSize = InputSize.Medium;

const inputSizeDataAttr = (size: InputSize) => `data-attr-size="${size}"`;

export const withInputSize = (size: InputSize) =>
  selectors.withSelector(`[${inputSizeDataAttr(size)}]`);

export const WithoutInputSizeDataAttr = Object.values(InputSize)
  .map(size => `:not([${inputSizeDataAttr(size)}])`)
  .join('');

export const muiInputLabelSizeClass = (size: InputSize): `.MuiInputLabel-size${string}` =>
  `.MuiInputLabel-size${size.charAt(0).toUpperCase() + size.slice(1)}`;

export const InputFontSize = '1rem';

/**
 * Additional right padding that should be added to the {@link Select} in order to accommodate for
 * the absolutely positioned caret icon within the {@link Select} component.
 */
const SelectInputCaretIconPaddingOffset = 12;

export const InputSizeProperties = {
  [InputSize.Large]: {
    fontSize: InputFontSize,
    height: 44,
    letterSpacing: 0.15,
    padding: {
      x: 14,
      y: 11,
    },
  },
  [InputSize.Medium]: {
    fontSize: InputFontSize,
    height: 40,
    padding: {
      x: 14,
      y: 9,
    },
  },
  [InputSize.Small]: {
    fontSize: InputFontSize,
    height: 36,
    padding: {
      x: 14,
      y: 7,
    },
  },
} as const satisfies Record<InputSize, Record<string, unknown>>;

type InputSizeStyleProperties<S extends InputSize = InputSize> = S extends InputSize
  ? (typeof InputSizeProperties)[S]
  : never;

type InputHeight<
  S extends InputSize = InputSize,
  Pixels extends boolean = true,
> = S extends InputSize
  ? Pixels extends true
    ? `${InputHeight<S, false>}px`
    : InputSizeStyleProperties<S>['height']
  : never;

export const Inputs = {
  getFontSize(size: InputSize = DefaultInputSize) {
    return InputSizeProperties[size].fontSize;
  },
  getHeight(size: InputSize = DefaultInputSize): InputHeight {
    return quantitativeSizeToString(InputSizeProperties[size].height, 'px');
  },
  getHorizontalPadding(size: InputSize = DefaultInputSize): `${number}px` {
    return quantitativeSizeToString(InputSizeProperties[size].padding.x, 'px');
  },
  /**
   * Returns the 'transform' CSS property that MUI applies to the label of an input element.
   *
   * MUI applies absolute positioning to the input's label, and uses the 'transform' property to
   * position the label within the input when it is not shrunk, which should center the label
   * vertically in the input and offset the label from the left the same distance that the input
   * text is offset from the left edge of the input.
   *
   * This can be done by transforming the input label based on the left and top padding of the
   * input.
   */
  getLabelTransform(size: InputSize = DefaultInputSize) {
    return (
      `translate(${this.getHorizontalPadding(size)}, ` +
      `${this.getVerticalPadding(size)}) scale(1)`
    );
  },
  getLineHeight(size: InputSize = DefaultInputSize): `${number}px` {
    return quantitativeSizeToString(
      InputSizeProperties[size].height - InputSizeProperties[size].padding.y * 2,
      'px',
    );
  },
  getPadding(size: InputSize = DefaultInputSize) {
    return [
      quantitativeSizeToString(this.getRawVerticalPadding(size), 'px'),
      quantitativeSizeToString(this.getRawHorizontalPadding(size), 'px'),
    ].join(' ');
  },
  getRawHeight(size: InputSize = DefaultInputSize): number {
    return InputSizeProperties[size].height;
  },
  getRawHorizontalPadding(size: InputSize = DefaultInputSize): number {
    return InputSizeProperties[size].padding.x;
  },
  getRawInnerHeight(size: InputSize = DefaultInputSize): number {
    return this.getRawHeight(size) - 2 * this.getRawVerticalPadding(size);
  },
  getRawPadding(size: InputSize = DefaultInputSize): { x: number; y: number } {
    return InputSizeProperties[size].padding;
  },
  getRawVerticalPadding(size: InputSize = DefaultInputSize): number {
    return InputSizeProperties[size].padding.y;
  },
  getSelectInnerHeight(size: InputSize = DefaultInputSize) {
    return quantitativeSizeToString(this.getRawInnerHeight(size), 'px');
  },
  /**
   * Returns the padding CSS value for a {@link Select} component that should be used for a given
   * custom (internal) input size, {@link InputSize}.  The padding of a {@link Select} should mirror
   * that of any other input of the same given {@link InputSize} with the only exception being the
   * right padding - which is increased to account for the dropdown caret in the {@link Select}.
   *
   * It is very important that the vertical padding remains consistent with other inputs so that the
   * {@link InputLabel} elements properly transform the label to be aligned in the input.
   */
  getSelectPadding(size: InputSize = DefaultInputSize) {
    return [
      quantitativeSizeToString(this.getRawVerticalPadding(size), 'px'),
      quantitativeSizeToString(
        this.getRawHorizontalPadding(size) + SelectInputCaretIconPaddingOffset,
        'px',
      ),
      quantitativeSizeToString(this.getRawVerticalPadding(size), 'px'),
      quantitativeSizeToString(this.getRawHorizontalPadding(size), 'px'),
    ].join(' ');
  },
  getVerticalPadding(size: InputSize = DefaultInputSize): `${number}px` {
    return quantitativeSizeToString(this.getRawVerticalPadding(size), 'px');
  },
};

/**
 * A value that is used to communicate to the styling applied in the global MUI theme that this
 * is an internalized {@link Autocomplete} component and not the base MUI {@link RootAutocomplete}
 * component, which is necessary for backwards compatibility while the old {@link RootAutocomplete}
 * still has to be supported.
 */
export const InternalizedAutocompleteType = 'internal-autocomplete';
